import { AlertCircle, Loader2 } from 'lucide-react'
import type { AgentEvent } from '@/lib/types'
import { EventDetailCard } from './EventDetailCard'
import { Skeleton } from '@/components/ui/skeleton'

export interface ExecutionEventTimelineProps {
  /** Array of agent events to display */
  events: AgentEvent[]
  /** Whether the events are currently loading */
  isLoading?: boolean
  /** Error message to display */
  error?: string
  /** Visual variant for event cards */
  variant?: 'default' | 'compact'
  /** Additional CSS classes */
  className?: string
}

function TimelineSkeleton() {
  return (
    <div data-testid="timeline-skeleton" className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center">
            <Skeleton className="w-6 h-6 rounded-full" />
            {i < 2 && <Skeleton className="w-0.5 h-16 mt-2" />}
          </div>
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <div className="text-center py-8 text-muted-foreground">
      <p>No events recorded for this run.</p>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2 p-4 border border-red-200 bg-red-50 dark:bg-red-950 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
      <AlertCircle className="h-5 w-5 flex-shrink-0" />
      <span>{message}</span>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex flex-col items-center gap-2 py-8">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Loading events...</span>
      <TimelineSkeleton />
    </div>
  )
}

/**
 * Displays a vertical timeline of agent execution events.
 * Shows events in chronological order with connecting lines.
 * 
 * @example
 * ```tsx
 * <ExecutionEventTimeline
 *   events={[
 *     { event_type: 'agent_started', timestamp: '...', metadata: {} },
 *     { event_type: 'file_modified', timestamp: '...', metadata: { file_path: '/src/App.tsx' } },
 *   ]}
 * />
 * ```
 */
export function ExecutionEventTimeline({
  events,
  isLoading = false,
  error,
  variant = 'default',
  className,
}: ExecutionEventTimelineProps) {
  if (isLoading) {
    return (
      <div className={className}>
        <LoadingState />
      </div>
    )
  }

  if (error) {
    return (
      <div className={className}>
        <ErrorState message={error} />
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className={className}>
        <EmptyState />
      </div>
    )
  }

  // Sort events chronologically
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )

  return (
    <div className={className}>
      <ul role="list" className="space-y-0">
        {sortedEvents.map((event, index) => (
          <li key={`${event.event_type}-${event.timestamp}-${index}`} className="relative">
            {/* Timeline connector line */}
            {index < sortedEvents.length - 1 && (
              <div
                data-testid="timeline-connector"
                className="absolute left-[11px] top-[28px] w-0.5 h-[calc(100%+8px)] bg-border"
              />
            )}
            
            <div className="relative pb-4">
              <EventDetailCard event={event} variant={variant} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
